// src/pages/FinanceTrackerPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { API_BASE as API } from "../config/constants";
import { formatStoredMoney } from "../utils/currency";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaArrowLeft,
  FaChartPie,
  FaPlus,
  FaSave,
  FaTrash,
  FaSyncAlt,
} from "react-icons/fa";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
  green: "#16a34a",
  red: "#dc2626",
};

const TABS = [
  { key: "expense", label: "Expense" },
  { key: "income", label: "Income" },
];
const encodeCategoryValue = (category = {}) =>
  category?.id ? `id:${category.id}` : `name:${category.name || ""}`;
const decodeCategoryValue = (value = "") => {
  if (!value) return { category_id: null, category_name: "" };
  if (value.startsWith("id:")) {
    const id = Number(value.slice(3));
    return { category_id: Number.isFinite(id) ? id : null, category_name: "" };
  }
  if (value.startsWith("name:")) {
    return { category_id: null, category_name: value.slice(5) };
  }
  return { category_id: null, category_name: value };
};

const FinanceTrackerPage = () => {
  const [userId, setUserId] = useState(null);
  const [activeTab, setActiveTab] = useState("expense");
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ expense: 0, income: 0, net: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // add category
  const [newCategory, setNewCategory] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);

  // add entry form
  const [categoryId, setCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [entryDate, setEntryDate] = useState(() => {
    const d = new Date();
    const pad = (v) => String(v).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  });
  const [notes, setNotes] = useState("");
  const [savingEntry, setSavingEntry] = useState(false);

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (data?.id) {
      setUserId(data.id);
    } else {
      toast.error("User not found. Please login again.");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, activeTab]);

  const loadAll = async () => {
    setRefreshing(true);
    await Promise.all([fetchSummary(), fetchCategories(activeTab), fetchEntries(activeTab)]);
    setRefreshing(false);
    setLoading(false);
  };

  const fetchSummary = async () => {
    try {
      const res = await fetch(`${API}/finance/summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: userId }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false)
        throw new Error(json?.message || "Failed to load summary");
      setSummary(json?.summary || { expense: 0, income: 0, net: 0 });
    } catch (e) {
      toast.error(e.message || "Unable to load summary");
    }
  };

  const fetchCategories = async (type) => {
    try {
      const res = await fetch(`${API}/finance/categories/list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: userId, entry_type: type }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false)
        throw new Error(json?.message || "Failed to load categories");
      setCategories(json?.categories || []);
    } catch (e) {
      toast.error(e.message || "Unable to load categories");
    }
  };

  const fetchEntries = async (type) => {
    try {
      const res = await fetch(`${API}/finance/entries/list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: userId, entry_type: type }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false)
        throw new Error(json?.message || "Failed to load entries");
      setEntries(json?.entries || []);
    } catch (e) {
      toast.error(e.message || "Unable to load entries");
    }
  };

  const handleAddCategory = async () => {
    const name = newCategory.trim();
    if (name.length < 3) {
      toast.info("Category name is too short");
      return;
    }
    try {
      setAddingCategory(true);
      const res = await fetch(`${API}/finance/categories/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: userId,
          entry_type: activeTab,
          name,
        }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false)
        throw new Error(json?.message || "Unable to add category");
      toast.success("Category added");
      setNewCategory("");
      fetchCategories(activeTab);
    } catch (e) {
      toast.error(e.message || "Failed to add category");
    } finally {
      setAddingCategory(false);
    }
  };

  const handleAddEntry = async () => {
    if (!categoryId) return toast.info("Pick a category");
    if (!amount || Number(amount) <= 0) return toast.info("Enter a valid amount");
    const { category_id, category_name } = decodeCategoryValue(categoryId);
    if (!category_id && !String(category_name || "").trim()) {
      return toast.info("Pick a category");
    }
    try {
      setSavingEntry(true);
      const res = await fetch(`${API}/finance/entries/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: userId,
          entry_type: activeTab,
          category_id: category_id || undefined,
          category_name: category_id ? undefined : category_name,
          amount: Number(amount),
          entry_date: entryDate,
          notes: notes.trim() || undefined,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false)
        throw new Error(
          json?.message ||
            (json?.errors ? Object.values(json.errors).flat().join(", ") : "") ||
            "Unable to save entry"
        );
      toast.success(`${activeTab === "expense" ? "Expense" : "Income"} saved`);
      setAmount("");
      setNotes("");
      setCategoryId("");
      fetchEntries(activeTab);
      fetchSummary();
    } catch (e) {
      toast.error(e.message || "Failed to save entry");
    } finally {
      setSavingEntry(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      const res = await fetch(`${API}/finance/entries/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entry_id: entryId, restaurantid: userId }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false)
        throw new Error(json?.message || "Unable to delete entry");
      toast.success("Deleted");
      setEntries((prev) => prev.filter((e) => e.id !== entryId));
      fetchSummary();
    } catch (e) {
      toast.error(e.message || "Failed to delete");
    }
  };

  const formatINR = (n) => formatStoredMoney(n, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const summaryCards = useMemo(
    () => [
      { title: "Total Expense", value: summary.expense || 0, accent: COLOR.red },
      { title: "Total Income", value: summary.income || 0, accent: COLOR.green },
      { title: "Net Balance", value: summary.net || 0, accent: summary.net >= 0 ? COLOR.green : COLOR.red },
    ],
    [summary]
  );

  if (loading) {
    return (
      <Center>
        <div className="spinner" />
        <div>Loading finance…</div>
      </Center>
    );
  }

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Top>
        <div>
          <Title>
            <FaChartPie /> Expense & Income
          </Title>
          <Sub>Pick a type, add categories, log entries.</Sub>
        </div>
        <PrimaryBtn onClick={loadAll}>
          <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
        </PrimaryBtn>
      </Top>

      <SummaryGrid>
        {summaryCards.map((c) => (
          <SummaryCard key={c.title}>
            <small>{c.title}</small>
            <strong style={{ color: c.accent }}>{formatINR(c.value)}</strong>
          </SummaryCard>
        ))}
      </SummaryGrid>

      <Tabs>
        {TABS.map((t) => {
          const active = activeTab === t.key;
          return (
            <Tab
              key={t.key}
              $active={active}
              $income={t.key === "income"}
              onClick={() => {
                setActiveTab(t.key);
                setCategoryId("");
                setNewCategory("");
              }}
            >
              <span>{t.key === "expense" ? "−" : "+"} {t.label}</span>
              <strong>{formatINR(summary[t.key] || 0)}</strong>
            </Tab>
          );
        })}
      </Tabs>

      <TypeHint $income={activeTab === "income"}>
        {activeTab === "income"
          ? "Income mode: add other income such as delivery fees, interest, commission, or miscellaneous receipts. Sales are calculated separately in Profit & Loss Report."
          : "Expense mode: add purchases, rent, salaries, utilities, and other business costs."}
      </TypeHint>

      <Grid>
        <Card>
          <CardTitle>Add Category</CardTitle>
          <InlineRow>
            <Input
              placeholder={`${activeTab === "expense" ? "Expense" : "Income"} category name`}
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              maxLength={40}
            />
            <PrimaryBtn onClick={handleAddCategory} disabled={addingCategory}>
              <FaPlus /> {addingCategory ? "Saving..." : "Add"}
            </PrimaryBtn>
          </InlineRow>
          <TagWrap>
            {categories.map((c) => (
              <Tag key={c.id || c.name}>{c.name}</Tag>
            ))}
            {!categories.length ? <Muted>No categories yet</Muted> : null}
          </TagWrap>
        </Card>

        <Card>
          <CardTitle>Add Entry</CardTitle>
          <FormGrid>
            <Field>
              <Label>Category</Label>
              <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c.id ? `id-${c.id}` : `name-${c.name}`} value={encodeCategoryValue(c)}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label>Amount</Label>
              <Input
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                placeholder="0"
                inputMode="decimal"
                pattern="[0-9.]*"
                maxLength={10}
              />
            </Field>
            <Field>
              <Label>Date</Label>
              <Input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
            </Field>
            <Field style={{ gridColumn: "span 2" }}>
              <Label>Notes</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional"
                maxLength={120}
              />
            </Field>
          </FormGrid>
          <PrimaryBtn onClick={handleAddEntry} disabled={savingEntry}>
            <FaSave /> {savingEntry ? "Saving..." : "Save Entry"}
          </PrimaryBtn>
        </Card>
      </Grid>

      <Card>
        <CardTitle>
          Recent {activeTab === "expense" ? "Expenses" : "Incomes"} ({entries.length})
        </CardTitle>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th style={{ width: 180 }}>Category</th>
                <th>Notes</th>
                <th style={{ width: 120 }}>Date</th>
                <th style={{ width: 120 }}>Amount</th>
                <th style={{ width: 90 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td>{e.category_name}</td>
                  <td>{e.notes || "—"}</td>
                  <td>{e.entry_date_formatted || e.entry_date}</td>
                  <td style={{ fontWeight: 800 }}>
                    {activeTab === "income" ? "+" : "-"}
                    {formatINR(e.amount)}
                  </td>
                  <td>
                    <DangerBtn onClick={() => handleDelete(e.id)}>
                      <FaTrash /> Delete
                    </DangerBtn>
                  </td>
                </tr>
              ))}
              {!entries.length && (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: 14, color: COLOR.muted }}>
                    No entries yet for this type.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableWrap>
      </Card>
    </Page>
  );
};

export default FinanceTrackerPage;

/* ---------------- styled ---------------- */
const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
`;
const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: ${COLOR.text};
  display: flex;
  gap: 8px;
  align-items: center;
`;
const Sub = styled.div`
  color: ${COLOR.muted};
`;
const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  background: ${COLOR.primary};
  color: #fff;
  font-weight: 800;
  cursor: pointer;
`;
const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
`;
const SummaryCard = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 12px;
  display: grid;
  gap: 6px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
  small {
    color: ${COLOR.muted};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  strong {
    font-size: 1.1rem;
  }
`;
const Tabs = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(180px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
  background: #eef2ff;
  padding: 6px;
  border-radius: 12px;
  border: 1px solid ${COLOR.line};
  width: min(100%, 620px);
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;
const Tab = styled.button`
  border: 1px solid ${({ $active, $income }) =>
    $active ? ($income ? "#86efac" : "#fca5a5") : "transparent"};
  background: ${({ $active, $income }) =>
    $active ? ($income ? "#f0fdf4" : "#fef2f2") : "transparent"};
  color: ${({ $active, $income }) =>
    $active ? ($income ? "#166534" : "#991b1b") : COLOR.text};
  font-weight: 800;
  padding: 11px 13px;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: ${({ $active }) => ($active ? "0 8px 16px rgba(0,0,0,0.06)" : "none")};
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:12px;
  strong{font-size:13px;}
`;
const TypeHint = styled.div`
  max-width: 850px;
  margin: 0 0 12px;
  padding: 11px 13px;
  border-radius: 11px;
  border: 1px solid ${({ $income }) => ($income ? "#bbf7d0" : "#fecaca")};
  background: ${({ $income }) => ($income ? "#f0fdf4" : "#fff7f7")};
  color: ${({ $income }) => ($income ? "#166534" : "#7f1d1d")};
  font-size: 13px;
  line-height: 1.45;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
`;
const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.05);
`;
const CardTitle = styled.h3`
  margin: 0 0 10px;
  color: ${COLOR.text};
`;
const InlineRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;
const Input = styled.input`
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  padding: 10px 12px;
  background: #f9f9f9;
  color: ${COLOR.text};
  outline: none;
  min-width: 0;
`;
const Select = styled.select`
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  padding: 10px 12px;
  background: #f9f9f9;
  color: ${COLOR.text};
  outline: none;
`;
const TagWrap = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
`;
const Tag = styled.span`
  background: #eef2ff;
  color: ${COLOR.text};
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid ${COLOR.line};
  font-weight: 700;
`;
const Muted = styled.span`
  color: ${COLOR.muted};
`;
const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 10px;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;
const Field = styled.div`
  display: grid;
  gap: 6px;
`;
const Label = styled.label`
  font-weight: 800;
  color: ${COLOR.text};
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid ${COLOR.line};
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid ${COLOR.line};
    text-align: left;
  }
  thead {
    background: #eef2ff;
  }
`;
const DangerBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid ${COLOR.red};
  background: #fff5f5;
  color: ${COLOR.red};
  font-weight: 700;
  cursor: pointer;
`;
const Center = styled.div`
  min-height: 40vh;
  display: grid;
  place-items: center;
  gap: 6px;
  color: ${COLOR.muted};
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: ${COLOR.primary};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
